import { defineStore } from 'pinia'
import { isEqual, cloneDeep } from 'lodash'

/** 标准化路径：去除数组下标 worldCup.matches[0].matchId → worldCup.matches.matchId */
const normalizeDataPath = (path) => {
  if (!path) return ''
  return path
    .split('.')
    .map((segment) => segment.replace(/\[\d*\]/g, '').replace('[]', ''))
    .filter((segment) => segment && !/^\d+$/.test(segment))
    .join('.')
}

/** 两条路径是否存在前缀/父子关系 */
const isPathPrefixRelated = (a, b) => {
  if (!a || !b) return false
  return a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`)
}

/** source.keys 链路到数组对象名（含 [] 段）的路径，如 worldCup.matches[].matchId → worldCup.matches */
const getKeysPathToArrayName = (key) => {
  const segments = key.split('.')
  let current = ''
  for (const segment of segments) {
    if (!/\[\]/.test(segment)) {
      const clean = segment.replace(/\[\d+\]/g, '')
      if (!clean || /^\d+$/.test(clean)) continue
      current = current ? `${current}.${clean}` : clean
      continue
    }
    const clean = segment.replace(/\[\d+\]/g, '').replace('[]', '')
    if (!clean || /^\d+$/.test(clean)) continue
    current = current ? `${current}.${clean}` : clean
    return normalizeDataPath(current)
  }
  return null
}

/** 递归收集 prev/next 之间变更的路径（dot notation） */
const collectChangedPaths = (prev, next, prefix = '') => {
  if (isEqual(prev, next)) return []

  if (prev == null || next == null || typeof prev !== 'object' || typeof next !== 'object') {
    return prefix ? [prefix] : []
  }

  if (Array.isArray(prev) || Array.isArray(next)) {
    if (!Array.isArray(prev) || !Array.isArray(next)) {
      return prefix ? [prefix] : []
    }
    // 数组内容变更，只记录到数组名称层级，如 worldCup.matches
    return prefix ? [prefix] : []
  }

  const changed = []
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)])
  for (const key of allKeys) {
    const childPrefix = prefix ? `${prefix}.${key}` : key
    changed.push(...collectChangedPaths(prev[key], next[key], childPrefix))
  }
  return changed
}

/** 判断绑定的 keys 是否落在变更路径的影响范围内（keys 为空由调用方处理） */
const isKeysAffectedByChanges = (keys, changedPaths) => {
  if (!changedPaths.length) return false

  const keyList = Array.isArray(keys) ? keys : [keys]
  return keyList.some((key) => {
    const normalizedKey = normalizeDataPath(key)
    if (!normalizedKey) return true
    const keysToArrayName = getKeysPathToArrayName(key)

    return changedPaths.some((changed) => {
      const normalizedChanged = normalizeDataPath(changed)
      if (isPathPrefixRelated(normalizedKey, normalizedChanged)) return true
      if (keysToArrayName && isPathPrefixRelated(keysToArrayName, normalizedChanged)) return true
      return false
    })
  })
}

const useAppStore = defineStore('app', {
  state: () => ({
    // 用户id
    uid: '',
    nickName: '',
    pkgName: '',
    language: '',
    requestLanguage: '',
    previewType: '',
    // 页面数据源
    pageDataSource: {},
    pageDataSourceCbMap: {},
    commonHeaders: {},
    unityToken: '',
    // 全局节点
    nodesMap: {},
    // 隐藏切换节点
    hiddenNodes: []
  }),
  getters: {},
  actions: {
    setHiddenNodes(nodes) {
      if (!nodes || !nodes.length) return
      this.hiddenNodes = nodes
    },
    setNodesMap(nodesMap) {
      this.nodesMap = nodesMap
      // console.log('setNodesMap', this.nodesMap)
    },
    setNodeAttribute(uuid, key, value) {
      if (!uuid || !key) return
      const nodeCb = this.nodesMap[uuid]
      if (!nodeCb) return
      nodeCb(key, value)
    },
    // 设置包名
    setPkgName(pkgName) {
      this.pkgName = pkgName
    },
    // 设置用户id
    setUid(uid) {
      this.uid = uid
    },
    // 设置用户昵称
    setNickName(name) {
      this.nickName = name
    },
    // 设置公用Headers
    setCommonHeaders(headers) {
      this.commonHeaders = { ...headers }
    },
    // 设置APP token
    setUnityToken(token) {
      this.unityToken = token
    },
    // 设置语言
    setLanguage(language) {
      this.language = language
    },
    // 设置请求语言
    setRequestLanguage(language) {
      this.requestLanguage = language
    },
    // 设置预览类型
    setPreviewType(previewType) {
      this.previewType = previewType
    },
    // 获取页面数据源
    getPageDataSource(uuid) {
      if (!uuid) return
      return this.pageDataSource[uuid]
    },
    // 设置页面数据源
    setPageDataSource(uuid, data) {
      if (!uuid || !data) return
      const prev = this.pageDataSource[uuid]
      data = cloneDeep(data)
      if (prev && isEqual(prev, data)) return

      const changedPaths = prev ? collectChangedPaths(prev, data) : null
      this.pageDataSource[uuid] = data

      if (this.pageDataSourceCbMap[uuid]) {
        const components = Object.keys(this.pageDataSourceCbMap[uuid])
        components.forEach((componentUUID) => {
          const { cb, keys } = this.pageDataSourceCbMap[uuid][componentUUID]
          if (!cb) return
          // 首次写入、无 keys 绑定时全量触发；否则仅触发变更路径相关的 cb
          if (!changedPaths || !keys || isKeysAffectedByChanges(keys, changedPaths)) {
            cb()
          }
        })
      }
    },
    // 设置数据源回调
    setPageDataSourceCbMap(uuid, componentUUID, cb, keys = null) {
      if (!uuid || !cb || !componentUUID) return
      if (!this.pageDataSourceCbMap[uuid]) {
        this.pageDataSourceCbMap[uuid] = {}
      }

      if (this.pageDataSourceCbMap[uuid][componentUUID]) {
        // 节点可能因显隐切换卸载后重挂（如弹框内绑定 swiperIndex 的子树），
        // 必须用最新实例的 cb 覆盖，否则数据源更新会调到已销毁实例的旧回调，导致界面不再响应
        this.pageDataSourceCbMap[uuid][componentUUID].cb = cb
        const entryKeys = this.pageDataSourceCbMap[uuid][componentUUID].keys
        if (keys && !entryKeys.includes(keys)) {
          entryKeys.push(keys)
        }
      } else {
        this.pageDataSourceCbMap[uuid][componentUUID] = { cb, keys: keys ? [keys] : [] }
      }

      if (this.pageDataSource[uuid]) {
        cb()
      }
    },
    // 清除数据源回调
    clearDataSourceCbMap() {
      // console.log('clearDataSourceCbMap')
      Object.keys(this.pageDataSourceCbMap).forEach((uuid) => {
        this.pageDataSourceCbMap[uuid] = []
      })
    },
    // 刷新数据源回调
    refreshCbMap() {
      // console.log('refreshCbMap', this.pageDataSource)
      Object.keys(this.pageDataSource).forEach((uuid) => {
        const data = this.pageDataSource[uuid]

        if (data && this.pageDataSourceCbMap[uuid]) {
          const components = Object.keys(this.pageDataSourceCbMap[uuid])
          if (components.length) {
            components.forEach((componentUUID) => {
              this.pageDataSourceCbMap[uuid][componentUUID]?.cb?.()
            })
            // console.log('----setPageDataSource----1', components.length)
          }
        }
      })
    }
  }
})

export default useAppStore
